import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarIcon, DownloadIcon, Loader2Icon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { exportRowsToCsv } from '../lib/csvExport'
import { addCalendarDays, todayDateInputValue } from '../lib/dateUtils'
import { formatAssigneeTeamLabel } from '../lib/jobAssignmentHistory'
import { resolveAssignedJobsScope, fetchJobsByIds } from '../lib/jobVisibility'
import { useDivision } from '../contexts/DivisionContext'
import { recordMatchesDivision } from '../lib/operationalDivisions'
import { MobilePage } from '../components/mobile'

interface EmployeeOption {
  id: string
  first_name?: string | null
  last_name?: string | null
}

interface ScheduleJob {
  id: string
  job_number: string
  title: string
  scheduled_date: string
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  status: string
  service_type?: string | null
  operational_division_id?: string | null
  crew?: { name?: string } | null
  employee?: { first_name?: string; last_name?: string } | null
  customer?: { company_name?: string } | null
  property?: { name?: string; address?: string } | null
  teamLabel?: string
}

const STATUS_BADGES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  pending_review: 'bg-purple-100 text-purple-800',
  under_review: 'bg-purple-100 text-purple-800',
  approved: 'bg-green-100 text-green-800',
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'incomplete', label: 'Pending (not completed)' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

const JOB_SELECT =
  'id, job_number, title, scheduled_date, scheduled_start_time, scheduled_end_time, status, service_type, operational_division_id, crew:crews(name), employee:employees(first_name, last_name), customer:customers(company_name), property:properties(name, address)'

function statusBadgeClass(status: string): string {
  return STATUS_BADGES[status] || 'bg-gray-100 text-gray-800'
}

function assigneeLabel(job: ScheduleJob): string {
  return (
    job.teamLabel ||
    job.crew?.name ||
    (job.employee
      ? `${job.employee.first_name || ''} ${job.employee.last_name || ''}`.trim()
      : '') ||
    'Unassigned'
  )
}

function propertyLabel(job: ScheduleJob): string {
  const name = job.property?.name || ''
  const address = job.property?.address || ''
  if (name && address && name !== address) return `${name} — ${address}`
  return address || name || ''
}

export function WorkerSchedule() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()

  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [workerId, setWorkerId] = useState('')
  const [startDate, setStartDate] = useState(todayDateInputValue())
  const [endDate, setEndDate] = useState(addCalendarDays(new Date(), 6))
  const [statusFilter, setStatusFilter] = useState('all')
  const [propertyFilter, setPropertyFilter] = useState('')

  const [jobs, setJobs] = useState<ScheduleJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    supabase
      .from('employees')
      .select('id, first_name, last_name')
      .order('first_name')
      .then(({ data }) => setEmployees((data || []) as EmployeeOption[]))
  }, [])

  const matchesStatus = useCallback(
    (status: string) => {
      if (statusFilter === 'all') return true
      if (statusFilter === 'incomplete') return status !== 'completed' && status !== 'cancelled'
      return status === statusFilter
    },
    [statusFilter]
  )

  const fetchJobs = useCallback(async () => {
    if (!startDate || !endDate) return
    try {
      setLoading(true)
      setError(null)

      let query = supabase
        .from('jobs')
        .select(JOB_SELECT)
        .gte('scheduled_date', startDate)
        .lte('scheduled_date', endDate)
        .order('scheduled_date', { ascending: true })

      if (statusFilter === 'incomplete') {
        query = query.not('status', 'in', '(completed,cancelled)')
      } else if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      let extraJobIds: string[] = []
      if (workerId) {
        const scope = await resolveAssignedJobsScope(workerId, {
          scheduledDateFrom: startDate,
          scheduledDateTo: endDate,
        })
        query = query.or(scope.orFilter)
        extraJobIds = scope.extraJobIds
      }

      const { data, error: err } = await query
      if (err) throw err

      const byId = new Map<string, any>()
      for (const row of data || []) byId.set(row.id, row)

      if (extraJobIds.length > 0) {
        const extras = await fetchJobsByIds(extraJobIds, JOB_SELECT)
        for (const row of extras as any[]) {
          const scheduled = String(row.scheduled_date || '')
          if (scheduled < startDate || scheduled > endDate) continue
          if (!matchesStatus(String(row.status || ''))) continue
          if (typeof row.id === 'string') byId.set(row.id, row)
        }
      }

      const mapped = Array.from(byId.values())
        .map((row: any) => ({
          ...row,
          customer: Array.isArray(row.customer) ? row.customer[0] || null : row.customer,
          property: Array.isArray(row.property) ? row.property[0] || null : row.property,
          crew: Array.isArray(row.crew) ? row.crew[0] || null : row.crew,
          employee: Array.isArray(row.employee) ? row.employee[0] || null : row.employee,
        }))
        .filter((job) => recordMatchesDivision(job, currentDivision)) as ScheduleJob[]

      // Multi-assignee label from active job_assignments (same approach as Dispatch Calendar).
      const ids = mapped.map((j) => j.id)
      const teamByJob: Record<string, string> = {}
      if (ids.length > 0) {
        const { data: assignRows } = await supabase
          .from('job_assignments')
          .select('job_id, crew:crews(name), employee:employees(first_name, last_name)')
          .in('job_id', ids)
          .is('unassigned_at', null)
        const grouped: Record<string, any[]> = {}
        for (const row of assignRows || []) {
          const r = {
            ...row,
            crew: Array.isArray(row.crew) ? row.crew[0] || null : row.crew,
            employee: Array.isArray(row.employee) ? row.employee[0] || null : row.employee,
          }
          if (!grouped[r.job_id]) grouped[r.job_id] = []
          grouped[r.job_id].push(r)
        }
        Object.keys(grouped).forEach((jobId) => {
          teamByJob[jobId] = formatAssigneeTeamLabel(grouped[jobId])
        })
      }

      mapped.sort((a, b) => {
        const dateCmp = String(a.scheduled_date).localeCompare(String(b.scheduled_date))
        if (dateCmp !== 0) return dateCmp
        return String(a.scheduled_start_time || '').localeCompare(String(b.scheduled_start_time || ''))
      })

      setJobs(mapped.map((j) => ({ ...j, teamLabel: teamByJob[j.id] || undefined })))
    } catch (err: any) {
      console.error('Error fetching schedule:', err)
      setError(err.message || 'Failed to load schedule')
      setJobs([])
    } finally {
      setLoading(false)
    }
  }, [workerId, startDate, endDate, statusFilter, matchesStatus, currentDivision])

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  const visibleJobs = propertyFilter
    ? jobs.filter((j) => propertyLabel(j).toLowerCase().includes(propertyFilter.toLowerCase()))
    : jobs

  const handleExportCsv = () => {
    exportRowsToCsv(
      `worker-schedule_${startDate}_${endDate}.csv`,
      [
        { label: 'Date', value: (j: ScheduleJob) => j.scheduled_date },
        { label: 'Time', value: (j: ScheduleJob) => j.scheduled_start_time || '' },
        { label: 'Job #', value: (j: ScheduleJob) => j.job_number },
        { label: 'Title', value: (j: ScheduleJob) => j.title },
        { label: 'Customer', value: (j: ScheduleJob) => j.customer?.company_name || '' },
        { label: 'Property', value: (j: ScheduleJob) => propertyLabel(j) },
        { label: 'Assigned To', value: (j: ScheduleJob) => assigneeLabel(j) },
        { label: 'Status', value: (j: ScheduleJob) => j.status },
      ],
      visibleJobs
    )
  }

  return (
    <MobilePage>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <CalendarIcon className="w-7 h-7 text-blue-600" />
          Worker Schedule
        </h1>
        <p className="mt-1 text-gray-600">
          Jobs by worker, property, and period — with each job&apos;s address and status
        </p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Worker</label>
            <select
              value={workerId}
              onChange={(e) => setWorkerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All workers</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {`${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Property</label>
            <input
              type="text"
              value={propertyFilter}
              onChange={(e) => setPropertyFilter(e.target.value)}
              placeholder="Filter by address..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">
            {loading ? 'Loading…' : `${visibleJobs.length} job${visibleJobs.length !== 1 ? 's' : ''}`}
          </h2>
          <button
            onClick={handleExportCsv}
            disabled={loading || visibleJobs.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DownloadIcon className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Time</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Job #</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Assigned To</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visibleJobs.map((job) => (
                  <tr
                    key={job.id}
                    onClick={() => navigate(`/jobs/details/${job.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                      {job.scheduled_date
                        ? new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })
                        : ''}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                      {job.scheduled_start_time ? job.scheduled_start_time.slice(0, 5) : '—'}
                    </td>
                    <td className="px-4 py-2 text-sm font-mono text-gray-600 whitespace-nowrap">{job.job_number}</td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{job.title}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{propertyLabel(job) || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">{assigneeLabel(job)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${statusBadgeClass(job.status)}`}
                      >
                        {String(job.status || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
                {visibleJobs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">
                      No jobs match these filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MobilePage>
  )
}
